import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Bot } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/525563335612';

// API URL - Use environment variable with fallback to production cloud endpoint
const API_URL = import.meta.env.VITE_API_BASE_URL
  ? `${import.meta.env.VITE_API_BASE_URL.replace(/\/+$/, '')}/chat`
  : 'https://cerebro.forcepfabrica.art/chat';

const GATEKEEPER_GREETING =
  'I am the Forcep Gatekeeper. Describe your mission or project.';

const CommsHUD = () => {
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'gatekeeper', text: GATEKEEPER_GREETING },
  ]);
  const [input, setInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const logEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (conciergeOpen) inputRef.current?.focus();
  }, [conciergeOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isSubmitting) return;

    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setInput('');
    setIsSubmitting(true);

    try {
      // REAL API CALL to Gemini via bridge_gemini.py (or your cloud-forwarded endpoint)
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: trimmed,
        }),
      });

      if (!response.ok) {
        throw new Error(`CONNECTION ERROR: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const reply = data.reply || 'Error: No response from AI Concierge.';

      setMessages((prev) => [
        ...prev,
        { role: 'gatekeeper', text: reply },
      ]);
    } catch (error) {
      console.error('Error calling AI Concierge:', error);
      const errorMessage = error.message || `CONNECTION ERROR: ${error}`;
      setMessages((prev) => [
        ...prev,
        { 
          role: 'gatekeeper', 
          text: errorMessage
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3 pointer-events-none">
      <div className="pointer-events-auto flex flex-col items-end gap-2">
        <AnimatePresence mode="wait">
          {conciergeOpen && (
            <motion.div
              key="concierge-panel"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 320 }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 180 }}
              className="overflow-hidden rounded-tl rounded-bl rounded-none"
            >
              <div
                className="w-[320px] h-[380px] flex flex-col rounded-tl rounded-bl rounded-none border border-white/10 bg-black/95 backdrop-blur-md shadow-2xl relative"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(0,255,65,0.04) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(0,255,65,0.04) 1px, transparent 1px)
                  `,
                  backgroundSize: '12px 12px',
                }}
              >
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/10 bg-black/40">
                  <div className="flex items-center gap-2">
                    <Bot size={14} className="text-terminal-green" />
                    <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-white/90">
                      AI Concierge · Secure Channel
                    </span>
                  </div>
                  <button
                    onClick={() => setConciergeOpen(false)}
                    className="p-1.5 rounded-none hover:bg-white/10 text-white/50 hover:text-white transition-colors font-mono text-sm"
                    aria-label="Close Concierge"
                  >
                    ×
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2.5 font-mono text-[11px] min-h-0 relative">
                  {/* AI Thinking Animation - Vibrating Wave Pattern */}
                  <AnimatePresence>
                    {isSubmitting && (
                      <motion.div
                        className="absolute inset-0 pointer-events-none z-10 overflow-hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {/* Gradient overlay for wave effect */}
                        <motion.div
                          className="absolute inset-0"
                          animate={{
                            background: [
                              'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.05) 35%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.05) 65%, transparent 100%)',
                              'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.08) 38%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.08) 62%, transparent 100%)',
                              'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.05) 35%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.05) 65%, transparent 100%)',
                            ],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                        />
                        {/* Diamond grid pattern - Vibrating wave effect */}
                        <div
                          className="absolute inset-0"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(16, 1fr)',
                            gridTemplateRows: 'repeat(8, 1fr)',
                            gap: '4px',
                            padding: '16px',
                          }}
                        >
                          {Array.from({ length: 128 }).map((_, i) => {
                            const row = Math.floor(i / 16);
                            const col = i % 16;
                            const centerX = 8;
                            const centerY = 4;
                            const distFromCenter = Math.sqrt(
                              Math.pow(col - centerX, 2) + Math.pow(row - centerY, 2)
                            );
                            const maxDist = Math.sqrt(Math.pow(8, 2) + Math.pow(4, 2));
                            const opacityBase = Math.max(0.08, 0.45 - (distFromCenter / maxDist) * 0.37);
                            const delay = ((row * 16 + col) * 0.025) % 1.6;
                            return (
                              <motion.div
                                key={i}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                                  backgroundColor: 'white',
                                }}
                                animate={{
                                  opacity: [
                                    opacityBase * 0.25,
                                    opacityBase * 2,
                                    opacityBase * 0.5,
                                    opacityBase * 2.2,
                                    opacityBase * 0.35,
                                    opacityBase * 0.25,
                                  ],
                                  scale: [0.8, 1.25, 0.9, 1.2, 0.85, 0.8],
                                }}
                                transition={{
                                  duration: 1.5,
                                  repeat: Infinity,
                                  delay: delay,
                                  ease: 'easeInOut',
                                }}
                              />
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={
                        m.role === 'gatekeeper'
                          ? 'text-terminal-green relative z-20'
                          : 'text-white/90 pl-3 border-l-2 border-white/20 relative z-20'
                      }
                    >
                      {m.role === 'gatekeeper' && (
                        <span className="text-white/40 text-[9px] uppercase tracking-wider block mb-0.5">
                          Gatekeeper
                        </span>
                      )}
                      {m.role === 'user' && (
                        <span className="text-white/40 text-[9px] uppercase tracking-wider block mb-0.5">
                          You
                        </span>
                      )}
                      <span className="leading-relaxed">{m.text}</span>
                    </div>
                  ))}
                  {isSubmitting && (
                    <div className="text-terminal-green/60 text-[9px] uppercase tracking-wider relative z-20">
                      Processing...
                    </div>
                  )}
                  <div ref={logEndRef} />
                </div>
                <form
                  onSubmit={sendMessage}
                  className="p-2.5 border-t border-white/10 bg-black/40"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Enter mission parameters..."
                    disabled={isSubmitting}
                    className="w-full bg-white/5 border border-white/10 rounded-none px-3 py-2 font-mono text-[10px] text-white placeholder-white/30 focus:outline-none focus:border-terminal-green/50 focus:ring-1 focus:ring-terminal-green/30"
                  />
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col items-end gap-2">
          {/* WhatsApp Uplink — top */}
          <motion.a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-end rounded-none"
            whileHover={{ scale: 1.02 }}
            initial={false}
          >
            <span className="font-mono text-[9px] uppercase tracking-widest text-white/70 whitespace-nowrap py-3 pr-2 pl-3 overflow-hidden max-w-0 group-hover:max-w-[220px] opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out">
              ESTABLISH SECURE LINE
            </span>
            <span className="flex items-center justify-center w-12 h-12 shrink-0 rounded-none bg-black/60 backdrop-blur-md border border-white/10 shadow-[0_0_20px_rgba(34,197,94,0.2)] hover:shadow-[0_0_24px_rgba(34,197,94,0.35)] hover:border-[#22c55e]/40 transition-all text-white/80 group-hover:text-[#22c55e]">
              <MessageCircle size={20} />
            </span>
          </motion.a>

          {/* AI Concierge — under WhatsApp */}
          <motion.button
            onClick={() => setConciergeOpen((o) => !o)}
            className="flex items-center justify-center w-12 h-12 rounded-none bg-black/60 backdrop-blur-md border border-white/10 shadow-lg hover:border-white/25 hover:bg-black/80 transition-colors text-white/80 hover:text-white"
            whileHover={{ scale: 1.02 }}
            aria-label="AI Concierge"
          >
            <Bot size={20} />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default CommsHUD;
